/**
 * useVoice — unified voice state machine for the conversation overlay.
 *
 * State: idle → requesting-mic → listening → idle
 *        idle → speaking → idle
 *
 * Different from useDictation (the simple inline-composer mic):
 *   • adds a 'speaking' state driven by TTS playback
 *   • supports auto-send-on-silence (TranscribeService.onSilence)
 *   • exposes a unified mute toggle wired to TtsPlaybackService
 *
 * The overlay component drives this: tap to start, speak, auto-send
 * fires on silence, agent reply text comes in via speak(), service
 * synthesises + plays it, state goes back to idle.
 *
 * Singleton-aware: shares getTranscribe()/getTts() module singletons
 * with the inline composer mic so we never have two captures running.
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import { getTranscribe } from '../services/voice/TranscribeService';
import { getTts } from '../services/voice/TtsPlaybackService';
import type { VoiceState } from '../types/voice';

interface UseVoiceReturn {
  voiceState: VoiceState;
  interimTranscript: string;
  error: string | null;
  isMuted: boolean;
  isSupported: boolean;
  /** Milliseconds left before the silence-auto-send fires. null = not
   *  currently counting down (idle, paused, or pre-first-word). Powers
   *  the countdown ring/number in the voice overlay. */
  silenceMsLeft: number | null;
  /** True while the user has tapped Pause — auto-send is suppressed
   *  but audio capture continues. */
  isSilencePaused: boolean;

  /** Start listening. onAutoSend fires when silence is detected. */
  startListening: (onAutoSend: (transcript: string) => void) => Promise<void>;
  /** Manually stop listening. Returns the final transcript. */
  stopListening: () => string;
  /** Cancel listening without firing onAutoSend. */
  cancelListening: () => void;
  /** Pause / resume the silence-auto-send countdown without stopping
   *  the mic. Lets the IC think mid-turn without their half-spoken
   *  sentence being shipped to the agent. */
  pauseSilence: () => void;
  resumeSilence: () => void;

  /** Speak text. Resolves when playback ends or is interrupted.
   *  onNaturalEnd fires only when the buffer plays through to the
   *  end — not when stopSpeaking() interrupted it. The conversation
   *  overlay uses this to auto-rearm listening for hands-free mode. */
  speak: (text: string, onNaturalEnd?: () => void) => Promise<void>;
  /** Stop any in-flight TTS. */
  stopSpeaking: () => void;
  toggleMute: () => void;
}

export function useVoice(): UseVoiceReturn {
  const [voiceState, setVoiceStateRaw] = useState<VoiceState>('idle');
  const [interimTranscript, setInterimTranscriptRaw] = useState('');
  const [error, setErrorRaw] = useState<string | null>(null);
  const [isMuted, setIsMutedRaw] = useState<boolean>(() => getTts().isMuted());
  const [isSupported] = useState<boolean>(() => getTranscribe().isSupported);
  const [silenceMsLeft, setSilenceMsLeftRaw] = useState<number | null>(null);
  const [isSilencePaused, setIsSilencePausedRaw] = useState(false);

  const mountedRef = useRef(true);
  // Wrap setters so post-unmount callbacks (Transcribe stream events,
  // TTS resolve) don't trigger the React "set state on unmounted" warning.
  const setVoiceState = useCallback((v: VoiceState) => { if (mountedRef.current) setVoiceStateRaw(v); }, []);
  const setInterimTranscript = useCallback((v: string) => { if (mountedRef.current) setInterimTranscriptRaw(v); }, []);
  const setError = useCallback((v: string | null) => { if (mountedRef.current) setErrorRaw(v); }, []);
  const setIsMuted = useCallback((v: boolean) => { if (mountedRef.current) setIsMutedRaw(v); }, []);
  const setSilenceMsLeft = useCallback((v: number | null) => { if (mountedRef.current) setSilenceMsLeftRaw(v); }, []);
  const setIsSilencePaused = useCallback((v: boolean) => { if (mountedRef.current) setIsSilencePausedRaw(v); }, []);

  useEffect(() => {
    mountedRef.current = true;
    // Pre-warm Cognito creds in the background so the first tap is snappy.
    getTranscribe().initialize().catch(() => { /* fallback handles it */ });
    return () => { mountedRef.current = false; };
  }, []);

  // Auto-clear errors after 4s so the UI doesn't strand stale messages
  useEffect(() => {
    if (!error) return;
    const t = setTimeout(() => {
      setError(null);
      setVoiceState('idle');
    }, 4000);
    return () => clearTimeout(t);
  }, [error, setError, setVoiceState]);

  // Cleanup: stop any active capture on unmount. TTS is shared — don't stop it.
  useEffect(() => {
    return () => {
      mountedRef.current = false;
      const stt = getTranscribe();
      if (stt.isRecording) stt.stopCapture();
    };
  }, []);

  const startListening = useCallback(async (onAutoSend: (transcript: string) => void) => {
    const stt = getTranscribe();
    if (stt.isRecording) return;

    if (!stt.isSupported) {
      setError('Voice input not available — your browser does not allow microphone access.');
      setVoiceState('error');
      return;
    }

    // Stop any agent voice that's still playing — barge-in friendly.
    getTts().stop();

    setVoiceState('requesting-mic');
    setInterimTranscript('');

    try {
      await stt.initialize();
      setSilenceMsLeft(null);
      setIsSilencePaused(false);
      await stt.startCapture(
        (text) => setInterimTranscript(text),
        (msg) => {
          setError(msg);
          setVoiceState('error');
          setSilenceMsLeft(null);
        },
        () => {
          // Silence detected → commit transcript + hand off to caller
          const transcript = stt.stopCapture();
          setVoiceState('idle');
          setInterimTranscript('');
          setSilenceMsLeft(null);
          if (transcript.trim()) onAutoSend(transcript);
        },
        // onSilenceTick — drives the visible countdown in the overlay.
        // Fires every ~200ms while the auto-send timer is counting
        // down; freezes (last-emitted value held) while paused.
        (msLeft) => setSilenceMsLeft(msLeft),
      );
      if (stt.isRecording) setVoiceState('listening');
    } catch (err: any) {
      const msg = err?.message || 'Microphone access denied';
      setError(msg);
      setVoiceState('error');
    }
  }, [setError, setInterimTranscript, setVoiceState, setSilenceMsLeft, setIsSilencePaused]);

  const pauseSilence = useCallback(() => {
    getTranscribe().pauseSilence();
    setIsSilencePaused(true);
  }, [setIsSilencePaused]);

  const resumeSilence = useCallback(() => {
    getTranscribe().resumeSilence();
    setIsSilencePaused(false);
  }, [setIsSilencePaused]);

  const stopListening = useCallback((): string => {
    const transcript = getTranscribe().stopCapture();
    setVoiceState('idle');
    setInterimTranscript('');
    setSilenceMsLeft(null);
    setIsSilencePaused(false);
    return transcript;
  }, [setInterimTranscript, setVoiceState, setSilenceMsLeft, setIsSilencePaused]);

  const cancelListening = useCallback(() => {
    getTranscribe().stopCapture();
    setVoiceState('idle');
    setInterimTranscript('');
    setSilenceMsLeft(null);
    setIsSilencePaused(false);
  }, [setInterimTranscript, setVoiceState, setSilenceMsLeft, setIsSilencePaused]);

  const speak = useCallback(async (text: string, onNaturalEnd?: () => void): Promise<void> => {
    if (!text.trim()) { onNaturalEnd?.(); return; }
    if (getTts().isMuted()) { onNaturalEnd?.(); return; }
    // Stop the mic so we don't capture our own TTS
    const stt = getTranscribe();
    if (stt.isRecording) stt.stopCapture();
    setVoiceState('speaking');
    try {
      await getTts().speakText(text);
      // Natural end — playback completed without being interrupted
      if (mountedRef.current) {
        setVoiceStateRaw((s) => (s === 'speaking' ? 'idle' : s));
      }
      onNaturalEnd?.();
    } catch (err: any) {
      // Whatever happened, drop out of 'speaking' so the UI doesn't strand
      if (mountedRef.current) {
        setVoiceStateRaw((s) => (s === 'speaking' ? 'idle' : s));
      }
      // err.code='stopped' is intentional (user tapped to interrupt or
      // closed the overlay) — silent. Anything else is worth logging.
      if (err?.code !== 'stopped') {
        console.warn('[useVoice] speak failed:', err?.message || err);
      }
    }
  }, [setVoiceState]);

  const stopSpeaking = useCallback(() => {
    getTts().stop();
    if (mountedRef.current) setVoiceStateRaw((s) => (s === 'speaking' ? 'idle' : s));
  }, []);

  const toggleMute = useCallback(() => {
    const next = getTts().toggleMuted();
    setIsMuted(next);
  }, [setIsMuted]);

  return {
    voiceState,
    interimTranscript,
    error,
    isMuted,
    isSupported,
    silenceMsLeft,
    isSilencePaused,
    startListening,
    stopListening,
    cancelListening,
    pauseSilence,
    resumeSilence,
    speak,
    stopSpeaking,
    toggleMute,
  };
}
